/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Internal dependencies
 */
import convertToCSS from './convertToCSS';
import generatePatternStyles from './generatePatternStyles';
import { getGradientGeometry, isGradientGeometryV2 } from './gradientGeometry';

function geometryKey(pattern) {
  return JSON.stringify([
    pattern?.geometryVersion ?? null,
    pattern?.start ?? null,
    pattern?.end ?? null,
    pattern?.type === 'radial' ? pattern.rotation || 0 : null,
  ]);
}

export default function isPatternEqual(p1, p2, patternType = undefined) {
  if (
    (patternType === undefined || patternType === 'background') &&
    isGradientGeometryV2(p1) &&
    isGradientGeometryV2(p2)
  ) {
    // Compare the values serialized by the renderer without building and
    // URI-encoding two complete SVG images on every picker movement.
    if (p1.type !== p2.type || geometryKey(p1) !== geometryKey(p2)) {
      return false;
    }
    const geometry1 = getGradientGeometry(p1);
    const geometry2 = getGradientGeometry(p2);
    return (
      Object.keys(geometry1).every(
        (key) => geometry1[key] === geometry2[key]
      ) &&
      p1.stops.length === p2.stops.length &&
      p1.stops.every(({ color, position }, index) => {
        const stop = p2.stops[index];
        return (
          position === stop.position &&
          color.r === stop.color.r &&
          color.g === stop.color.g &&
          color.b === stop.color.b &&
          (color.a ?? 1) * (p1.alpha ?? 1) ===
            (stop.color.a ?? 1) * (p2.alpha ?? 1)
        );
      })
    );
  }
  const p1CSS = convertToCSS(generatePatternStyles(p1, patternType));
  const p2CSS = convertToCSS(generatePatternStyles(p2, patternType));
  return p1CSS === p2CSS && geometryKey(p1) === geometryKey(p2);
}
